import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
import { useSelector } from 'react-redux';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper, InputAdornment } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { getTemperatureUnit } from 'src/module/authentication/selector';
import { pairUp } from '../Commissioning/utils';
import { celsiusToFahrenheitConvertor, fahrenheitToCelsiusConvertor } from 'src/utils/utils';

// These field lists define the order and set of fields rendered in each info table section.
// They are hardcoded because circuit form fields are fixed by product design — they do not
// vary per facility or device, so fetching them from the backend would add complexity with no benefit.
const PROJECT_INFO_FIELDS = [
  'project', 'area', 'cableType', 'ehtTag',
  'numTraces', 'manufacturer', 'controlPanel', 'distributionPanel',
  'controllerHtc', 'circuitBreaker', 'numJb', 'numRtd',
  'ehtIso', 'isoRevision',
];

const CONTROLLER_CONFIG_FIELDS = [
  'address', 'lowTempAlarm', 'controlMode', 'highCurrentAlarm',
  'setpointTemp', 'lowCurrentAlarm', 'groundFaultAlarm', 'groundFaultTripSetpoint',
];

const TEMP_FIELDS = new Set(['setpointTemp', 'lowTempAlarm']);
const CURRENT_FIELDS = new Set(['highCurrentAlarm', 'lowCurrentAlarm']);
const GFI_FIELDS = new Set(['groundFaultAlarm', 'groundFaultTripSetpoint']);


export default function CircuitInfo (props) {
  const { data, onChange, readOnly } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);
  const temperatureUnit = useSelector(getTemperatureUnit);

  const [projectInfo, setProjectInfo] = useState(Map(data?.get('projectInfo') || {}));
  const [controllerConfig, setControllerConfig] = useState(Map(data?.get('controllerConfig') || {}));
  // Holds raw user input for temp fields while editing. On blur we convert F→C and commit.
  // This avoids round-trip precision drift and preserves intermediate states like "7." or "-".
  const [tempInputs, setTempInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    setProjectInfo(Map(data?.get('projectInfo') || {}));
    setControllerConfig(Map(data?.get('controllerConfig') || {}));
    setTempInputs({});
  }, [data]);

  useEffect(() => {
    setTempInputs({});
  }, [temperatureUnit]);

  const onProjectChange = (key, val) => {
    const updated = projectInfo.set(key, val);
    setProjectInfo(updated);
    onChange(['extras', 'projectInfo', key], val);
  };

  const onControllerChange = (key, val) => {
    const updated = controllerConfig.set(key, val);
    setControllerConfig(updated);
    onChange(['extras', 'controllerConfig', key], val);
  };

  const onTempFieldChange = (key, displayVal) => {
    setTempInputs(prev => ({ ...prev, [key]: displayVal }));
  };

  const onTempFieldBlur = (key) => {
    const displayVal = tempInputs[key];
    if (displayVal === undefined) return;
    const numeric = parseFloat(displayVal);
    const storedVal = temperatureUnit === 'F' && !isNaN(numeric)
      ? String(fahrenheitToCelsiusConvertor(numeric))
      : displayVal;
    onControllerChange(key, storedVal);
    setTempInputs(prev => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // If user is actively editing, show their raw text. Otherwise show the stored °C value converted to the display unit.
  const getTempDisplayValue = (key) => {
    if (key in tempInputs) return tempInputs[key];
    const stored = controllerConfig.get(key) || '';
    if (temperatureUnit === 'F') {
      const numeric = parseFloat(stored);
      return isNaN(numeric) ? stored : String(celsiusToFahrenheitConvertor(numeric));
    }
    return stored;
  };

  const projectPairs = pairUp(PROJECT_INFO_FIELDS);
  const controllerPairs = pairUp(CONTROLLER_CONFIG_FIELDS);

  const renderControllerField = (field) => {
    if (TEMP_FIELDS.has(field)) {
      return (
        <TextField
          fullWidth
          disabled={readOnly}
          value={getTempDisplayValue(field)}
          onChange={(e) => onTempFieldChange(field, e.target.value)}
          onBlur={() => onTempFieldBlur(field)}
          InputProps={{
            endAdornment: <InputAdornment position='end'>°{temperatureUnit}</InputAdornment>,
          }}
        />
      );
    }

    if (CURRENT_FIELDS.has(field)) {
      return (
        <TextField
          fullWidth
          disabled={readOnly}
          value={controllerConfig.get(field) || ''}
          onChange={(e) => onControllerChange(field, e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position='end'>A</InputAdornment>,
          }}
        />
      );
    }

    if (GFI_FIELDS.has(field)) {
      return (
        <TextField
          fullWidth
          disabled={readOnly}
          value={controllerConfig.get(field) || ''}
          onChange={(e) => onControllerChange(field, e.target.value)}
          InputProps={{
            endAdornment: <InputAdornment position='end'>mA</InputAdornment>,
          }}
        />
      );
    }

    return (
      <TextField
        fullWidth
        disabled={readOnly}
        value={controllerConfig.get(field) || ''}
        onChange={(e) => onControllerChange(field, e.target.value)}
      />
    );
  };

  return (
    <div>
      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t('commissioningCircuit.modalTitle')}</h3>
        <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projectPairs.map(([fieldA, fieldB]) => (
                <TableRow key={fieldA}>
                  <TableCell className='sub-headers'>{t(`commissioningCircuit.table.${fieldA}`)}</TableCell>
                  <TableCell className='body-cell'>
                    <TextField
                      fullWidth
                      disabled={readOnly}
                      value={projectInfo.get(fieldA) || ''}
                      onChange={(e) => onProjectChange(fieldA, e.target.value)}
                    />
                  </TableCell>
                  {fieldB ? (
                    <>
                      <TableCell className='sub-headers'>{t(`commissioningCircuit.table.${fieldB}`)}</TableCell>
                      <TableCell className='body-cell'>
                        <TextField
                          fullWidth
                          disabled={readOnly}
                          value={projectInfo.get(fieldB) || ''}
                          onChange={(e) => onProjectChange(fieldB, e.target.value)}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell />
                      <TableCell />
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t('commissioningCircuit.controllerConfig.title')}</h3>
        <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {controllerPairs.map(([fieldA, fieldB]) => (
                <TableRow key={fieldA}>
                  <TableCell className='sub-headers'>{t(`commissioningCircuit.controllerConfig.${fieldA}`)}</TableCell>
                  <TableCell className='body-cell'>{renderControllerField(fieldA)}</TableCell>
                  {fieldB ? (
                    <>
                      <TableCell className='sub-headers'>{t(`commissioningCircuit.controllerConfig.${fieldB}`)}</TableCell>
                      <TableCell className='body-cell'>{renderControllerField(fieldB)}</TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell />
                      <TableCell />
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

CircuitInfo.propTypes = {
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

CircuitInfo.defaultProps = {
  readOnly: false,
};
